/**
 * PTEL Text Learning Store v1.0
 * 
 * Sistema de almacenamiento persistente con IndexedDB (Dexie.js)
 * para patrones de texto aprendidos adaptativamente.
 * 
 * Replica la arquitectura de PatternLearningStore.ts pero para:
 * - FALSE_POSITIVES (palabras que no deben modificarse)
 * - PROTECTED_ABBREVIATIONS (E.S., P.N., C.P., etc.)
 * - DICCIONARIO_PTEL (términos válidos de infraestructuras)
 * - CORRECCIONES (Trasformador → Transformador)
 * 
 * Características:
 * - IndexedDB para almacenamiento persistente (>50MB capacidad)
 * - Migración automática desde datos hardcodeados
 * - Cache LRU en memoria para rendimiento
 * - Estadísticas de uso por categoría
 * - Exportación/importación de patrones
 * - Carga automática de datos semilla
 * 
 * CHANGELOG v1.0 (08-Dic-2025):
 * - NEW: Estructura base replicando PatternLearningStore
 * - NEW: Migración de datos desde textDeconcatenator.ts
 * - NEW: Sistema de aprendizaje adaptativo para texto
 * 
 * @version 1.0.0
 * @date Diciembre 2025
 */

import Dexie, { type Table } from 'dexie';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/**
 * Entrada de palabra/término en el sistema de aprendizaje
 */
export interface TextEntry {
  id: string;
  value: string;
  category: 'false_positive' | 'abbreviation' | 'dictionary' | 'correction';
  source: 'seed' | 'user' | 'auto' | 'community';
  uses: number;
  lastUsed: string;
  firstSeen: string;
  isStable: boolean;
  municipalities: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Corrección ortográfica de texto
 */
export interface TextCorrection {
  id: string;
  from: string;
  to: string;
  source: 'seed' | 'user' | 'auto' | 'community';
  uses: number;
  lastUsed: string;
  firstSeen: string;
  isStable: boolean;
  caseSensitive: boolean;
  municipalities: string[];
}

/**
 * Estadísticas por categoría
 */
export interface TextCategoryStats {
  category: string;
  totalEntries: number;
  stableEntries: number;
  totalUses: number;
  lastUpdated: string;
}

/**
 * Evento de aprendizaje de texto
 */
export interface TextLearningEvent {
  id?: number;
  timestamp: string;
  tipo: 'entry_added' | 'entry_used' | 'entry_failed' | 'correction_applied' | 'seeds_loaded';
  entryId?: string;
  category?: string;
  municipio?: string;
  detalles: Record<string, unknown>;
}

// ============================================================================
// DATOS SEMILLA
// ============================================================================

/**
 * Palabras que contienen patrones que parecen concatenaciones pero no lo son.
 * Migrado de textDeconcatenator.ts v2.7.0
 */
export const SEED_FALSE_POSITIVES: string[] = [
  // Palabras compuestas
  'polideportivo', 'videojuego', 'videoconferencia', 'radiodifusión',
  'biodegradable', 'audiovisual', 'hidroeléctrica', 'termodinámico',
  'electromagnético', 'socioeconómico', 'medioambiental', 'iberoamericano',
  
  // Palabras con "de" interno
  'residencial', 'modelo', 'moderno', 'moderna', 'federal', 'confederal',
  'moderado', 'moderada', 'modelado', 'modelaje', 'remodelación',
  
  // Palabras con "del" interno  
  'adelante', 'delante', 'delantera', 'delantero', 'modelista',
  
  // Palabras con "la" interno
  'lateral', 'bilateral', 'multilateral', 'colateral', 'unilateral',
  
  // Palabras con "el" interno
  'elemento', 'elemental', 'electoral', 'eléctrico', 'eléctrica',
  'delegación', 'delegado', 'delegada', 'modelar',
  
  // Palabras con "al" interno
  'mineral', 'general', 'comercial', 'industrial', 'material',
  'medicinal', 'racional', 'nacional', 'regional',

  // Nombres propios con "el"
  'manuel', 'manuela', 'miguel', 'gabriel', 'gabriela',
  'rafael', 'rafaela', 'israel', 'ezequiel', 'daniel', 'daniela',
  'joel', 'abel', 'ismael', 'samuel', 'misael', 'eliseo',
  'rogelio', 'aurelio', 'aurelia', 'cornelio', 'cornelia',
  'heliodoro', 'fidel', 'olegario',
  
  // Nombres propios con "la"
  'candela', 'candelaria', 'adela', 'adelaida', 'adelina',
  'estela', 'angela', 'angelita', 'angelica', 'angeles',
  'micaela', 'graciela', 'carmela', 'carmelita', 'carmelo',
  'consuela', 'consuelo', 'gisela', 'mariela', 'pamela',
  'noela', 'noelia', 'marcela', 'marcelo', 'ornela',
  'ariela', 'ariel', 'marisela',
  
  // Apellidos y topónimos
  'varela', 'vilela', 'cela', 'pela',
  
  // Topónimos andaluces con preposiciones internas (BUG-PARSE-002)
  'atalaya', 'villalba', 'villaluenga', 'villamartin',
  'villanueva', 'villaverde', 'villahermosa', 'villalonga',
  'aldea', 'aldeanueva', 'aldeamayor', 'alcala', 'alcalá',
];

/**
 * Abreviaturas protegidas que NO deben separarse.
 * Migrado de textDeconcatenator.ts v2.7.0
 */
export const SEED_PROTECTED_ABBREVIATIONS: string[] = [
  // Gasolineras y estaciones
  'E.S.', 'ES.', 
  // Parques naturales
  'P.N.', 'PN.',
  // Códigos postales
  'C.P.', 'CP.',
  // Centros educativos
  'C.E.I.P.', 'CEIP.', 'I.E.S.', 'IES.',
  // Carreteras
  'C.N.', 'CN.', 'A.',
  // Otros
  'S.A.', 'SA.', 'S.L.', 'SL.', 'S.C.', 'SC.',
  'D.', 'Dª.', 'Dr.', 'Dra.',
  'Sr.', 'Sra.', 'Srta.',
  'M.ª', 'Mª.',
  'Av.', 'C/.',
];

/**
 * Diccionario de palabras clave para separar ALL CAPS concatenados.
 * Migrado de textDeconcatenator.ts v2.7.0
 */
export const SEED_DICTIONARY_PTEL: string[] = [
  // =========================================================================
  // TIPOLOGÍAS PRINCIPALES (documentos PTEL oficiales)
  // =========================================================================
  
  // Sanitario
  'CONSULTORIO', 'HOSPITAL', 'AMBULATORIO', 'CLINICA', 'CLÍNICA',
  'CENTRO', 'SALUD', 'URGENCIAS', 'FARMACIA',
  
  // Residencial/Asistencial
  'RESIDENCIA', 'ANCIANOS', 'MAYORES', 'TERCERA', 'EDAD',
  'DISCAPACITADOS', 'DEPENDIENTES', 'ASISTIDOS',
  
  // Educativo
  'COLEGIO', 'INSTITUTO', 'ESCUELA', 'GUARDERIA', 'GUARDERÍA',
  'CEIP', 'IES', 'CPR', 'CEP', 'INFANTIL', 'PRIMARIA', 'SECUNDARIA',
  'UNIVERSIDAD', 'FACULTAD', 'CONSERVATORIO',
  
  // Cultural
  'BIBLIOTECA', 'MUSEO', 'TEATRO', 'AUDITORIO', 'CULTURAL',
  'ARCHIVO', 'SALA', 'EXPOSICIONES', 'CASA', 'CULTURA',
  
  // Deportivo
  'POLIDEPORTIVO', 'PISCINA', 'ESTADIO', 'PABELLON', 'PABELLÓN',
  'CAMPO', 'FUTBOL', 'FÚTBOL', 'TENIS', 'PADEL', 'PÁDEL',
  'GIMNASIO', 'PISTA', 'ATLETISMO',
  
  // Espacios públicos
  'PLAZA', 'PARQUE', 'JARDIN', 'JARDÍN', 'JARDINES',
  'PASEO', 'AVENIDA', 'CALLE', 'CAMINO', 'CARRETERA',
  'GLORIETA', 'ROTONDA', 'MIRADOR',
  
  // Servicios municipales
  'AYUNTAMIENTO', 'MUNICIPAL', 'MUNICIPALES',
  'CEMENTERIO', 'MERCADO', 'LONJA', 'MATADERO',
  'DEPOSITO', 'DEPÓSITO', 'ALMACEN', 'ALMACÉN',
  'TALLER', 'NAVE', 'NAVES',
  
  // Seguridad
  'CUARTEL', 'GUARDIA', 'CIVIL', 'POLICIA', 'POLICÍA',
  'LOCAL', 'BOMBEROS', 'PROTECCION', 'PROTECCIÓN',
  'EMERGENCIAS', 'EMERGENCIA', 'SEGURIDAD',
  
  // Transporte
  'ESTACION', 'ESTACIÓN', 'AUTOBUSES', 'AUTOBUS', 'AUTOBÚS',
  'TREN', 'FERROCARRIL', 'TAXI', 'PARADA', 'APEADERO',
  
  // Suministros
  'TRANSFORMADOR', 'TRASFORMADOR', 'ELECTRICA', 'ELÉCTRICA',
  'AGUA', 'POTABLE', 'ETAP', 'EDAR',
  'GASOLINERA', 'GASODUCTO', 'ANTENA', 'REPETIDOR',
  
  // Comercio / actividad económica
  'PANADERIA', 'PANADERÍA', 'CARNICERIA', 'CARNICERÍA',
  'FARMACIA', 'FERRETERIA', 'FERRETERÍA',
  'INSTALACIONES', 'INSTALACION', 'INSTALACIÓN',
  
  // =========================================================================
  // MODIFICADORES Y ADJETIVOS
  // =========================================================================
  'PUBLICO', 'PÚBLICO', 'PUBLICA', 'PÚBLICA',
  'PRINCIPAL', 'CENTRAL', 'GENERAL', 'NUEVO', 'NUEVA',
  'VIEJO', 'VIEJA', 'ANTIGUO', 'ANTIGUA',
  'MEDICO', 'MÉDICO', 'MEDICA', 'MÉDICA',
  'COMARCAL', 'PROVINCIAL', 'REGIONAL', 'NACIONAL',
  'SOCIAL', 'CIVICO', 'CÍVICO',
  
  // =========================================================================
  // NOMBRES PROPIOS FRECUENTES EN PTEL
  // =========================================================================
  
  // Religiosos (muy comunes)
  'SAN', 'SANTA', 'SANTO', 'VIRGEN', 'CRISTO', 'SEÑOR', 'SEÑORA',
  'CARMEN', 'JOSE', 'JOSÉ', 'JUAN', 'PEDRO', 'PABLO', 'MARIA', 'MARÍA',
  'FRANCISCO', 'ANTONIO', 'MANUEL', 'MIGUEL', 'RAFAEL', 'GABRIEL',
  'ANGELES', 'ÁNGELES', 'ROSARIO', 'DOLORES', 'PILAR', 'MERCEDES',
  'JOAQUIN', 'JOAQUÍN', 'FERNANDO', 'ALFONSO', 'ANDRES', 'ANDRÉS',
  
  // Apellidos/títulos comunes
  'HERMANOS', 'HERMANAS', 'DOCTOR', 'DOCTORA', 'PROFESOR', 'PROFESORA',
  'ALCALDE', 'CONDE', 'DUQUE', 'MARQUES', 'MARQUÉS', 'REY', 'REINA',
  'PEÑA', 'GARCIA', 'GARCÍA', 'LOPEZ', 'LÓPEZ', 'MARTINEZ', 'MARTÍNEZ',
  'SANCHEZ', 'SÁNCHEZ', 'FERNANDEZ', 'FERNÁNDEZ', 'GONZALEZ', 'GONZÁLEZ',
  
  // Geográficos andaluces
  'SIERRA', 'VALLE', 'RIO', 'RÍO', 'MONTE', 'CERRO', 'LLANO', 'VEGA',
  'FUENTE', 'POZO', 'CRUZ', 'PUENTE', 'TORRE',
  
  // Provincias/ciudades
  'ALMERIA', 'ALMERÍA', 'GRANADA', 'MALAGA', 'MÁLAGA',
  'JAEN', 'JAÉN', 'CORDOBA', 'CÓRDOBA', 'SEVILLA', 'HUELVA', 'CADIZ', 'CÁDIZ',
  
  // =========================================================================
  // CÓDIGOS Y ABREVIATURAS
  // =========================================================================
  'CP', 'CTRA', 'AV', 'CL', 'PL', 'PZ', 'URB', 'POL', 'IND',
  'NUM', 'KM', 'HA',
  
  // Preposiciones (para separación)
  'DE', 'DEL', 'LA', 'EL', 'LOS', 'LAS', 'AL', 'EN', 'CON',
];

/**
 * Correcciones ortográficas conocidas
 * Migrado de textDeconcatenator.ts v2.7.0
 */
export const SEED_CORRECTIONS: Array<{ from: string; to: string }> = [
  { from: 'Trasformador', to: 'Transformador' },
  { from: 'trasformador', to: 'transformador' },
  { from: 'TRASFORMADOR', to: 'TRANSFORMADOR' },
];

/**
 * GEO-04: Patrones de formato basados en regex
 * 
 * Patrones que limpian formato de origen (Markdown, HTML, etc.) que
 * contamina datos y causa fallos en APIs de geocodificación.
 * 
 * Estructura:
 * - id: Identificador único del patrón
 * - name: Nombre descriptivo
 * - pattern: Expresión regular (string para serialización)
 * - replacement: Cadena de reemplazo ($1, $2, etc.)
 * - source: Origen del patrón (LOTE_A, LOTE_B, etc.)
 * - confidence: Confianza 0-100
 * - occurrences: Veces encontrado en corpus
 * - description: Explicación del problema que resuelve
 */
export interface FormatPattern {
  id: string;
  name: string;
  pattern: string;
  replacement: string;
  source: string[];
  confidence: number;
  occurrences: number;
  description: string;
}

export const SEED_FORMAT_PATTERNS: FormatPattern[] = [
  {
    id: 'ASTERISCOS_MARKDOWN',
    name: 'Asteriscos Markdown (bold/italic)',
    pattern: '\\*{1,2}([^*]+)\\*{1,2}',
    replacement: '$1',
    source: ['LOTE_B_DIC2025'],
    confidence: 98,
    occurrences: 25,
    description: 'Elimina **bold** e *italic* de exportaciones GEA que contaminan nombres y direcciones. Ej: "**Hospital**" → "Hospital"'
  },
  {
    id: 'UNDERSCORES_MARKDOWN',
    name: 'Underscores Markdown (bold/italic)',
    pattern: '_{1,2}([^_]+)_{1,2}',
    replacement: '$1',
    source: ['LOTE_B_DIC2025'],
    confidence: 95,
    occurrences: 3,
    description: 'Elimina __bold__ e _italic_ alternativo. Menos común que asteriscos.'
  },
  {
    id: 'ENLACES_MARKDOWN',
    name: 'Enlaces Markdown',
    pattern: '\\[([^\\]]+)\\]\\([^)]+\\)',
    replacement: '$1',
    source: ['LOTE_B_DIC2025'],
    confidence: 99,
    occurrences: 8,
    description: 'Extrae texto de enlaces [texto](url). Preserva el texto visible.'
  },
];

// ============================================================================
// BASE DE DATOS DEXIE
// ============================================================================

class PTELTextLearningDatabase extends Dexie {
  entries!: Table<TextEntry>;
  corrections!: Table<TextCorrection>;
  stats!: Table<TextCategoryStats>;
  events!: Table<TextLearningEvent>;

  constructor() {
    super('PTELTextLearningDB');
    
    this.version(1).stores({
      entries: 'id, value, category, source, isStable, *municipalities',
      corrections: 'id, from, to, source, isStable',
      stats: 'category',
      events: '++id, timestamp, tipo, entryId, category'
    });
  }
}

// ============================================================================
// SINGLETON Y CACHE
// ============================================================================

let db: PTELTextLearningDatabase | null = null;
const entryCache: Map<string, TextEntry> = new Map();
const correctionCache: Map<string, TextCorrection> = new Map();

// Sets para búsqueda rápida O(1)
const falsePositivesSet: Set<string> = new Set();
const abbreviationsSet: Set<string> = new Set();
const dictionarySet: Set<string> = new Set();
let setsInitialized = false;

const CACHE_MAX_SIZE = 500;
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutos

const cacheTimestamps: Map<string, number> = new Map();

/**
 * Asegura que los Sets estén inicializados (sync, desde constantes SEED)
 * Esto permite que las funciones de consulta funcionen inmediatamente
 * sin necesidad de esperar la inicialización de la base de datos.
 */
function ensureSetsInitialized(): void {
  if (setsInitialized) return;
  
  // Inicializar desde SEED constants (síncrono)
  for (const word of SEED_FALSE_POSITIVES) {
    falsePositivesSet.add(word.toLowerCase());
  }
  
  for (const abbr of SEED_PROTECTED_ABBREVIATIONS) {
    abbreviationsSet.add(abbr.toUpperCase());
  }
  
  for (const term of SEED_DICTIONARY_PTEL) {
    dictionarySet.add(term.toUpperCase());
  }
  
  setsInitialized = true;
}

/**
 * Obtiene la instancia de la base de datos
 */
export async function getTextDatabase(): Promise<PTELTextLearningDatabase> {
  if (!db) {
    db = new PTELTextLearningDatabase();
    await db.open();
    await loadSeedDataIfNeeded();
    await buildLookupSets();
  }
  return db;
}

// ============================================================================
// DATOS SEMILLA
// ============================================================================

const SEEDS_LOADED_FLAG = 'ptel_text_seeds_loaded_v1';

/**
 * Carga los datos semilla si no se han cargado antes
 */
async function loadSeedDataIfNeeded(): Promise<void> {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    
    if (localStorage.getItem(SEEDS_LOADED_FLAG) === 'true') {
      return;
    }
    
    await loadTextSeeds();
    localStorage.setItem(SEEDS_LOADED_FLAG, 'true');
  } catch (error) {
    console.warn('Error cargando datos semilla de texto:', error);
  }
}

/**
 * Carga los datos semilla desde las constantes
 */
export async function loadTextSeeds(): Promise<{ 
  falsePositives: number; 
  abbreviations: number; 
  dictionary: number; 
  corrections: number 
}> {
  const database = await getTextDatabase();
  const now = new Date().toISOString();
  
  let falsePositives = 0;
  let abbreviations = 0;
  let dictionary = 0;
  let corrections = 0;
  
  // FALSE_POSITIVES
  for (const word of SEED_FALSE_POSITIVES) {
    const id = `fp_${normalizeForId(word)}`;
    const existing = await database.entries.get(id);
    if (!existing) {
      await database.entries.put({
        id,
        value: word,
        category: 'false_positive',
        source: 'seed',
        uses: 0,
        lastUsed: now,
        firstSeen: now,
        isStable: true,
        municipalities: [],
      });
      falsePositives++;
    }
  }
  
  // PROTECTED_ABBREVIATIONS
  for (const abbr of SEED_PROTECTED_ABBREVIATIONS) {
    const id = `abbr_${normalizeForId(abbr)}`;
    const existing = await database.entries.get(id);
    if (!existing) {
      await database.entries.put({
        id,
        value: abbr,
        category: 'abbreviation',
        source: 'seed',
        uses: 0,
        lastUsed: now,
        firstSeen: now,
        isStable: true,
        municipalities: [],
      });
      abbreviations++;
    }
  }
  
  // DICCIONARIO_PTEL
  for (const term of SEED_DICTIONARY_PTEL) {
    const id = `dict_${normalizeForId(term)}`;
    const existing = await database.entries.get(id);
    if (!existing) {
      await database.entries.put({
        id,
        value: term,
        category: 'dictionary',
        source: 'seed',
        uses: 0,
        lastUsed: now,
        firstSeen: now,
        isStable: true,
        municipalities: [],
      });
      dictionary++;
    }
  }
  
  // CORRECTIONS
  for (const corr of SEED_CORRECTIONS) {
    const id = `corr_${normalizeForId(corr.from)}_${normalizeForId(corr.to)}`;
    const existing = await database.corrections.get(id);
    if (!existing) {
      await database.corrections.put({
        id,
        from: corr.from,
        to: corr.to,
        source: 'seed',
        uses: 0,
        lastUsed: now,
        firstSeen: now,
        isStable: true,
        caseSensitive: true,
        municipalities: [],
      });
      corrections++;
    }
  }
  
  if (falsePositives + abbreviations + dictionary + corrections > 0) {
    console.log(`✅ Cargados datos semilla de texto: ${falsePositives} FP, ${abbreviations} ABBR, ${dictionary} DICT, ${corrections} CORR`);
    
    await logTextEvent({
      timestamp: now,
      tipo: 'seeds_loaded',
      detalles: { falsePositives, abbreviations, dictionary, corrections }
    });
  }
  
  // Reconstruir sets
  await buildLookupSets();
  
  return { falsePositives, abbreviations, dictionary, corrections };
}

/**
 * Construye los Sets de búsqueda rápida desde la DB
 */
async function buildLookupSets(): Promise<void> {
  const database = await getTextDatabase();
  
  falsePositivesSet.clear();
  abbreviationsSet.clear();
  dictionarySet.clear();
  
  const entries = await database.entries.toArray();
  
  for (const entry of entries) {
    switch (entry.category) {
      case 'false_positive':
        falsePositivesSet.add(entry.value.toLowerCase());
        break;
      case 'abbreviation':
        abbreviationsSet.add(entry.value.toUpperCase());
        break;
      case 'dictionary':
        dictionarySet.add(entry.value.toUpperCase());
        break;
    }
  }
  
  setsInitialized = true;
}

// ============================================================================
// FUNCIONES DE CONSULTA RÁPIDA (O(1))
// ============================================================================

/**
 * Verifica si una palabra es un falso positivo conocido
 */
export function isFalsePositive(word: string): boolean {
  ensureSetsInitialized();
  return falsePositivesSet.has(word.toLowerCase());
}

/**
 * Verifica si un texto es una abreviatura protegida
 */
export function isProtectedAbbreviation(text: string): boolean {
  ensureSetsInitialized();
  return abbreviationsSet.has(text.toUpperCase());
}

/**
 * Verifica si una palabra está en el diccionario PTEL
 */
export function isInDictionary(word: string): boolean {
  ensureSetsInitialized();
  return dictionarySet.has(word.toUpperCase());
}

/**
 * Obtiene el diccionario ordenado por longitud (para matching greedy)
 */
export function getDictionarySortedByLength(): string[] {
  ensureSetsInitialized();
  return Array.from(dictionarySet).sort((a, b) => b.length - a.length);
}

/**
 * Obtiene todas las abreviaturas protegidas
 */
export function getProtectedAbbreviations(): Set<string> {
  ensureSetsInitialized();
  return new Set(abbreviationsSet);
}

/**
 * Obtiene todos los falsos positivos
 */
export function getFalsePositives(): Set<string> {
  ensureSetsInitialized();
  return new Set(falsePositivesSet);
}

// ============================================================================
// GESTIÓN DE ENTRADAS
// ============================================================================

/**
 * Añade una nueva entrada al sistema
 */
export async function addTextEntry(
  value: string,
  category: TextEntry['category'],
  source: TextEntry['source'] = 'user',
  municipio?: string
): Promise<TextEntry> {
  const database = await getTextDatabase();
  const now = new Date().toISOString();
  
  const id = `${category.substring(0, 4)}_${normalizeForId(value)}`;
  
  const existing = await database.entries.get(id);
  if (existing) {
    // Actualizar uso
    existing.uses++;
    existing.lastUsed = now;
    if (municipio && !existing.municipalities.includes(municipio)) {
      existing.municipalities.push(municipio);
    }
    await database.entries.put(existing);
    updateEntryCache(existing);
    return existing;
  }
  
  const entry: TextEntry = {
    id,
    value,
    category,
    source,
    uses: 1,
    lastUsed: now,
    firstSeen: now,
    isStable: false,
    municipalities: municipio ? [municipio] : [],
  };
  
  await database.entries.put(entry);
  updateEntryCache(entry);
  
  // Actualizar set correspondiente
  switch (category) {
    case 'false_positive':
      falsePositivesSet.add(value.toLowerCase());
      break;
    case 'abbreviation':
      abbreviationsSet.add(value.toUpperCase());
      break;
    case 'dictionary':
      dictionarySet.add(value.toUpperCase());
      break;
  }
  
  await logTextEvent({
    timestamp: now,
    tipo: 'entry_added',
    entryId: id,
    category,
    municipio,
    detalles: { value, source }
  });
  
  return entry;
}

/**
 * Obtiene una entrada por ID
 */
export async function getTextEntry(id: string): Promise<TextEntry | undefined> {
  // Verificar cache
  const cached = entryCache.get(id);
  const timestamp = cacheTimestamps.get(id);
  
  if (cached && timestamp && (Date.now() - timestamp) < CACHE_TTL_MS) {
    return cached;
  }
  
  const database = await getTextDatabase();
  const entry = await database.entries.get(id);
  
  if (entry) {
    updateEntryCache(entry);
  }
  
  return entry;
}

/**
 * Obtiene todas las entradas de una categoría
 */
export async function getEntriesByCategory(category: TextEntry['category']): Promise<TextEntry[]> {
  const database = await getTextDatabase();
  return database.entries.where('category').equals(category).toArray();
}

/**
 * Actualiza el uso de una entrada
 */
export async function updateTextEntryUsage(
  id: string,
  success: boolean,
  municipio?: string
): Promise<void> {
  const database = await getTextDatabase();
  const entry = await database.entries.get(id);
  
  if (!entry) return;
  
  entry.uses++;
  entry.lastUsed = new Date().toISOString();
  
  if (municipio && !entry.municipalities.includes(municipio)) {
    entry.municipalities.push(municipio);
  }
  
  // Marcar como estable si tiene suficientes usos
  if (entry.uses >= 10 && !entry.isStable) {
    entry.isStable = true;
  }
  
  await database.entries.put(entry);
  updateEntryCache(entry);
  
  await logTextEvent({
    timestamp: new Date().toISOString(),
    tipo: success ? 'entry_used' : 'entry_failed',
    entryId: id,
    category: entry.category,
    municipio,
    detalles: { uses: entry.uses }
  });
}

/**
 * Elimina una entrada
 */
export async function deleteTextEntry(id: string): Promise<void> {
  const database = await getTextDatabase();
  const entry = await database.entries.get(id);
  
  if (entry) {
    // Eliminar del set correspondiente
    switch (entry.category) {
      case 'false_positive':
        falsePositivesSet.delete(entry.value.toLowerCase());
        break;
      case 'abbreviation':
        abbreviationsSet.delete(entry.value.toUpperCase());
        break;
      case 'dictionary':
        dictionarySet.delete(entry.value.toUpperCase());
        break;
    }
  }
  
  await database.entries.delete(id);
  entryCache.delete(id);
  cacheTimestamps.delete(id);
}

// ============================================================================
// GESTIÓN DE CORRECCIONES
// ============================================================================

/**
 * Añade una corrección ortográfica
 */
export async function addTextCorrection(
  from: string,
  to: string,
  caseSensitive: boolean = true,
  municipio?: string
): Promise<TextCorrection> {
  const database = await getTextDatabase();
  const now = new Date().toISOString();
  
  const id = `corr_${normalizeForId(from)}_${normalizeForId(to)}`;
  
  const existing = await database.corrections.get(id);
  if (existing) {
    existing.uses++;
    existing.lastUsed = now;
    if (municipio && !existing.municipalities.includes(municipio)) {
      existing.municipalities.push(municipio);
    }
    await database.corrections.put(existing);
    return existing;
  }
  
  const correction: TextCorrection = {
    id,
    from,
    to,
    source: 'user',
    uses: 1,
    lastUsed: now,
    firstSeen: now,
    isStable: false,
    caseSensitive,
    municipalities: municipio ? [municipio] : [],
  };
  
  await database.corrections.put(correction);
  correctionCache.set(id, correction);
  
  return correction;
}

/**
 * Obtiene todas las correcciones
 */
export async function getAllCorrections(): Promise<TextCorrection[]> {
  const database = await getTextDatabase();
  return database.corrections.toArray();
}

/**
 * Busca una corrección para un texto
 */
export async function findCorrection(text: string): Promise<TextCorrection | undefined> {
  const database = await getTextDatabase();
  
  // Búsqueda exacta primero
  const corrections = await database.corrections.toArray();
  
  for (const corr of corrections) {
    if (corr.caseSensitive) {
      if (text === corr.from) return corr;
    } else {
      if (text.toLowerCase() === corr.from.toLowerCase()) return corr;
    }
  }
  
  return undefined;
}

// ============================================================================
// EVENTOS Y LOG
// ============================================================================

/**
 * Registra un evento de aprendizaje
 */
async function logTextEvent(event: TextLearningEvent): Promise<void> {
  try {
    const database = await getTextDatabase();
    await database.events.add(event);
    
    // Limitar a 1000 eventos
    const count = await database.events.count();
    if (count > 1000) {
      const oldest = await database.events.orderBy('id').limit(count - 1000).toArray();
      await database.events.bulkDelete(oldest.map(e => e.id!));
    }
  } catch (error) {
    console.warn('Error registrando evento de texto:', error);
  }
}

/**
 * Obtiene eventos recientes
 */
export async function getRecentTextEvents(limit: number = 50): Promise<TextLearningEvent[]> {
  const database = await getTextDatabase();
  return database.events
    .orderBy('id')
    .reverse()
    .limit(limit)
    .toArray();
}

// ============================================================================
// EXPORTACIÓN E IMPORTACIÓN
// ============================================================================

export interface TextExportData {
  version: string;
  exportDate: string;
  entries: TextEntry[];
  corrections: TextCorrection[];
}

/**
 * Exporta todos los datos de texto
 */
export async function exportTextData(): Promise<TextExportData> {
  const database = await getTextDatabase();
  
  return {
    version: '1.0.0',
    exportDate: new Date().toISOString(),
    entries: await database.entries.toArray(),
    corrections: await database.corrections.toArray(),
  };
}

/**
 * Importa datos de texto
 */
export async function importTextData(
  data: TextExportData,
  mode: 'merge' | 'replace' = 'merge'
): Promise<{ imported: number; skipped: number }> {
  const database = await getTextDatabase();
  let imported = 0;
  let skipped = 0;
  
  if (mode === 'replace') {
    await database.entries.clear();
    await database.corrections.clear();
  }
  
  for (const entry of data.entries) {
    const existing = await database.entries.get(entry.id);
    if (!existing || mode === 'replace') {
      await database.entries.put(entry);
      imported++;
    } else {
      skipped++;
    }
  }
  
  for (const correction of data.corrections) {
    const existing = await database.corrections.get(correction.id);
    if (!existing || mode === 'replace') {
      await database.corrections.put(correction);
    }
  }
  
  // Reconstruir sets
  await buildLookupSets();
  
  return { imported, skipped };
}

// ============================================================================
// ESTADÍSTICAS
// ============================================================================

/**
 * Obtiene estadísticas del sistema de texto
 */
export async function getTextLearningStats(): Promise<{
  falsePositives: number;
  abbreviations: number;
  dictionary: number;
  corrections: number;
  totalEntries: number;
  stableEntries: number;
  cacheSize: number;
}> {
  const database = await getTextDatabase();
  
  const entries = await database.entries.toArray();
  const corrections = await database.corrections.toArray();
  
  const falsePositives = entries.filter(e => e.category === 'false_positive').length;
  const abbreviations = entries.filter(e => e.category === 'abbreviation').length;
  const dictionary = entries.filter(e => e.category === 'dictionary').length;
  const stableEntries = entries.filter(e => e.isStable).length;
  
  return {
    falsePositives,
    abbreviations,
    dictionary,
    corrections: corrections.length,
    totalEntries: entries.length,
    stableEntries,
    cacheSize: entryCache.size + correctionCache.size,
  };
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Normaliza un valor para usarlo como ID
 */
function normalizeForId(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '_')
    .substring(0, 50);
}

/**
 * Actualiza el cache de entradas
 */
function updateEntryCache(entry: TextEntry): void {
  entryCache.set(entry.id, entry);
  cacheTimestamps.set(entry.id, Date.now());
  
  if (entryCache.size > CACHE_MAX_SIZE) {
    evictOldestCacheEntry();
  }
}

/**
 * Elimina la entrada más antigua del cache
 */
function evictOldestCacheEntry(): void {
  let oldestKey: string | null = null;
  let oldestTime = Infinity;
  
  for (const [key, timestamp] of cacheTimestamps.entries()) {
    if (timestamp < oldestTime) {
      oldestTime = timestamp;
      oldestKey = key;
    }
  }
  
  if (oldestKey) {
    entryCache.delete(oldestKey);
    cacheTimestamps.delete(oldestKey);
  }
}

/**
 * Limpia el cache en memoria
 */
export function clearTextCache(): void {
  entryCache.clear();
  correctionCache.clear();
  cacheTimestamps.clear();
}

/**
 * Fuerza la recarga de datos semilla
 */
export async function forceReloadTextSeeds(): Promise<{ 
  falsePositives: number; 
  abbreviations: number; 
  dictionary: number; 
  corrections: number 
}> {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(SEEDS_LOADED_FLAG);
  }
  return loadTextSeeds();
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Base de datos
  getTextDatabase,
  
  // Carga de semillas
  loadTextSeeds,
  forceReloadTextSeeds,
  
  // Consultas rápidas O(1)
  isFalsePositive,
  isProtectedAbbreviation,
  isInDictionary,
  getDictionarySortedByLength,
  getProtectedAbbreviations,
  getFalsePositives,
  
  // Gestión de entradas
  addTextEntry,
  getTextEntry,
  getEntriesByCategory,
  updateTextEntryUsage,
  deleteTextEntry,
  
  // Gestión de correcciones
  addTextCorrection,
  getAllCorrections,
  findCorrection,
  
  // Eventos
  getRecentTextEvents,
  
  // Estadísticas
  getTextLearningStats,
  
  // Export/Import
  exportTextData,
  importTextData,
  
  // Utilidades
  clearTextCache,
};
